import type { RefObject } from "react";
import { useRef, useEffect, useState } from "react";

export interface IUseFocusProps {
    isFocused: boolean;
}

/**
 * useFocus - хук для отслеживания фокуса на элементе.
 *
 * @param ref - ref на отслеживаемый элемент
 * @param defaultValue - значение фокуса по умолчанию
 * @param skipMouseFocus - пропускает изменение isFocused если нажимаем мышкой (альтернатива для focus-visible)
 * @returns {isFocused} - Объект с состоянием isFocused.
 */
export const useFocus = (
    ref: RefObject<HTMLElement>,
    defaultValue: boolean = false,
    skipMouseFocus: boolean = true,
): IUseFocusProps => {
    const [isFocused, setIsFocused] = useState<boolean>(defaultValue);
    const skipFocusRef = useRef<boolean>(false);
    const skipMouseFocusRef = useRef<boolean>(skipMouseFocus);

    useEffect(() => {
        const currentRef = ref.current;

        const focusHandler = () => {
            if (!skipFocusRef.current) setIsFocused(true);
        };

        const onBlur = () => {
            if (!skipFocusRef.current) setIsFocused(false);
        };

        const mouseDownHandler = () => {
            if (skipMouseFocusRef.current) skipFocusRef.current = true;
        };

        const mouseUpHandler = () => {
            if (skipMouseFocusRef.current) skipFocusRef.current = false;
        };

        currentRef?.addEventListener("focus", focusHandler);
        currentRef?.addEventListener("blur", onBlur);
        currentRef?.addEventListener("mousedown", mouseDownHandler);
        currentRef?.addEventListener("mouseup", mouseUpHandler);

        return () => {
            currentRef?.removeEventListener("focus", focusHandler);
            currentRef?.removeEventListener("blur", onBlur);
            currentRef?.removeEventListener("mousedown", mouseDownHandler);
            currentRef?.removeEventListener("mouseup", mouseUpHandler);
        };
    }, [ref]);

    return { isFocused };
};
